import type { CachedBiteMetadata, LessonEntryContext } from "./resumeContracts";

export interface SessionDiscoveryData {
  is_existing_session?: boolean;
  resume_available?: boolean;
  resume_runtime_available?: boolean;
  state_version?: number | string | null;
  client_flags?: Record<string, unknown> | null;
  has_exit_ticket?: boolean;
  exit_ticket_state?: string | null;
  has_pre_assessment?: boolean;
  pre_assessment_state?: string | null;
  cached_bite?: CachedBiteMetadata | null;
  [key: string]: unknown;
}

export interface DiscoveryRouteBase {
  sessionExists: boolean;
  resumeRuntimeAvailable: boolean;
}

export interface ResumeRestartRouteDecision extends DiscoveryRouteBase {
  kind: "resume_restart_modal";
  reason: "explicit_resume" | "existing_session";
  hasResumeCheckpoint: boolean;
  stateVersion: number;
}

export interface InitializationRouteDecision extends DiscoveryRouteBase {
  kind: "initialization_modal";
  reason: "runtime_unavailable" | "new_session";
}

export interface LearnAgainRouteDecision extends DiscoveryRouteBase {
  kind: "learn_again_modal";
}

export interface PreviewRouteDecision extends DiscoveryRouteBase {
  kind: "preview_start";
}

export type DiscoveryRouteDecision =
  | PreviewRouteDecision
  | LearnAgainRouteDecision
  | ResumeRestartRouteDecision
  | InitializationRouteDecision;

const routeBase = (
  data: SessionDiscoveryData,
  entryContext: LessonEntryContext,
): DiscoveryRouteBase => {
  const resumeRuntimeAvailable = data.resume_runtime_available !== false;
  return {
    sessionExists: Boolean(
      data.is_existing_session && entryContext.initialSlideIndex === null,
    ),
    resumeRuntimeAvailable,
  };
};

export const resolveSessionDiscoveryRoute = ({
  data,
  entryContext,
  isPreview,
}: {
  data: SessionDiscoveryData;
  entryContext: LessonEntryContext;
  isPreview: boolean;
}): DiscoveryRouteDecision => {
  const base = routeBase(data, entryContext);

  if (isPreview) {
    return { ...base, kind: "preview_start" };
  }

  if (entryContext.entryIntent === "learn_again") {
    return { ...base, kind: "learn_again_modal" };
  }

  const stateVersion = Number(data.state_version || 0);
  const hasResumeCheckpoint = data.resume_available === true;
  // Which assessments may be retaken is resolved by the caller, from the same
  // discovery payload — see resolveRetakeAssessments.
  const resumeRestartPayload = { hasResumeCheckpoint, stateVersion };

  if (
    entryContext.entryIntent === "resume" &&
    base.sessionExists &&
    base.resumeRuntimeAvailable
  ) {
    return {
      ...base,
      kind: "resume_restart_modal",
      reason: "explicit_resume",
      ...resumeRestartPayload,
    };
  }

  if (!base.resumeRuntimeAvailable) {
    return {
      ...base,
      kind: "initialization_modal",
      reason: "runtime_unavailable",
    };
  }

  if (base.sessionExists) {
    return {
      ...base,
      kind: "resume_restart_modal",
      reason: "existing_session",
      ...resumeRestartPayload,
    };
  }

  return {
    ...base,
    kind: "initialization_modal",
    reason: "new_session",
  };
};
